import { ChangeEvent, useEffect, useState } from "react";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { Button } from "@remo-co/ui-core/src/components/Button";
import { Card } from "@remo-co/ui-core/src/components/Card";
import { Container } from "@remo-co/ui-core/src/components/Container";
import { Input } from "@remo-co/ui-core/src/components/Input";
import { Loader } from "@remo-co/ui-core/src/components/Loader";
import { Theme } from "@remo-co/ui-core/src/types";
import { makeStyles } from "@remo-co/ui-core/src/utils/makeStyles";
import { useI18n } from "i18n";
import useQueryParams from "modules/queryParams/hooks/useQueryParams";
import { PAGES, usePageTracking } from "modules/tracking";
import useAuth from "../hooks/useAuth";
import { PASSWORD_DEFAULTS } from "../constants";
import usePasswordValidation from "../hooks/usePasswordValidation";

const styles = makeStyles<Theme>(() => ({
  container: {
    height: "100vh",
  },
}));

const PasswordChange = () => {
  const { t } = useI18n(["common", "auth"]);
  const [password, setPassword] = useState({
    error: false,
    value: "",
    message: "",
  });
  const [repeatPassword, setRepeatPassword] = useState({
    error: false,
    value: "",
    message: "",
  });
  const { validatePassword } = usePasswordValidation();
  const { confirmPasswordReset, isLoading } = useAuth();
  const { oobCode, continueUrl } = useQueryParams();
  const classes = styles({});

  usePageTracking(PAGES.CHANGE_PASSWORD);

  const handlePasswordChange = (e: ChangeEvent<HTMLInputElement>) =>
    setPassword({ value: e.target.value, error: false, message: "" });

  const handleRepeatPasswordChange = (e: ChangeEvent<HTMLInputElement>) =>
    setRepeatPassword({ value: e.target.value, error: false, message: "" });

  useEffect(() => {
    if (password.value.length > PASSWORD_DEFAULTS.MAX_LENGTH) {
      setPassword({
        value: password.value,
        error: true,
        message: t("auth:max.password.limit", {
          length: PASSWORD_DEFAULTS.MAX_LENGTH,
        }),
      });
    } else {
      setPassword({ value: password.value, error: false, message: "" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [password.value]);

  useEffect(() => {
    if (repeatPassword.value.length > PASSWORD_DEFAULTS.MAX_LENGTH) {
      setRepeatPassword({
        value: repeatPassword.value,
        error: true,
        message: t("auth:max.password.limit", {
          length: PASSWORD_DEFAULTS.MAX_LENGTH,
        }),
      });
    } else {
      setRepeatPassword({
        value: repeatPassword.value,
        error: false,
        message: "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repeatPassword.value]);

  const onSubmit = () => {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    if (!validate()) {
      return;
    }
    confirmPasswordReset(
      oobCode as string,
      password.value,
      continueUrl as string,
    );
  };

  const validate = () => {
    const { error, value, message } = validatePassword(password.value);

    if (error) {
      setPassword({
        error,
        value,
        message,
      });

      return false;
    }

    if (password.value !== repeatPassword.value) {
      setRepeatPassword({
        error: true,
        value: repeatPassword.value,
        message: t("auth:password.math.fail"),
      });

      return false;
    }

    return true;
  };

  return (
    <Container
      flex
      column
      className={classes.container}
      alignItems="center"
      justifyContent="center"
    >
      <Typography variant="h1">{t("auth:welcome.back")}</Typography>
      <Container top="md" fullWidth={false}>
        <Card
          title={isLoading ? null : t("auth:change.password")}
          style={{ minWidth: "350px" }}
        >
          {isLoading ? (
            <Loader />
          ) : (
            <>
              <Input
                id="password"
                data-testid="password-input"
                required
                name="password"
                type="password"
                fullWidth
                size="sm"
                value={password.value}
                onChange={handlePasswordChange}
                label={t("auth:new.password")}
                error={password.message}
              />
              <Container top="sm">
                <Input
                  label={t("auth:retype.password")}
                  data-testid="repeat-password-input"
                  id="repeatPassword"
                  name="repeatPassword"
                  fullWidth
                  size="sm"
                  type="password"
                  value={repeatPassword.value}
                  onChange={handleRepeatPasswordChange}
                  error={repeatPassword.message}
                />
              </Container>
              <Container top="md">
                <Button
                  variant="primary"
                  data-testid="submit-password"
                  color="blue"
                  fullWidth
                  onClick={onSubmit}
                  disabled={
                    password.error ||
                    repeatPassword.error ||
                    !password.value ||
                    !repeatPassword.value
                  }
                >
                  {t("auth:submit")}
                </Button>
              </Container>
            </>
          )}
        </Card>
      </Container>
    </Container>
  );
};

export default PasswordChange;
